import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  useGridApiRef,
} from "@mui/x-data-grid";
import exportFromJSON from "export-from-json";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import PasswordIcon from "@mui/icons-material/Password";
import EmailIcon from "@mui/icons-material/Email";

import JBWDateLabel from "./JBWDateLabel";
import Avatar from "react-avatar";
import CustomNoRowsOverlay from "./gridNoRowOverLay";
import StarsIcon from '@mui/icons-material/Stars';
import {
  EXPORT_STATUS_ALL,
  EXPORT_STATUS_NONE,
  EXPORT_STATUS_VALUES,
  STATUS_ALL,
  STATUS_ACTIVE,
  STATUS_INACTIVE,
  buildExportRows,
  filterUsers,
  isMailable,
} from "../utils/userList";
import { getFilteredGridRows } from "../utils/gridExport";

const QuickSearchToolbar = ({
  statusFilter,
  onStatusFilterChange,
  dealerIdFilter,
  onDealerIdFilterChange,
  exportStatusFilter,
  onExportStatusFilterChange,
  onExportClick,
}) => {
  return (
    <GridToolbarContainer
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        width: "100%",
        // Headroom for the Status select's floating label, and clearance
        // between the row and the column headers.
        px: 1,
        pt: 2,
        pb: 1.5,
      }}
    >
      {/*
        debounceMs={0} because Export takes a synchronous snapshot of the grid's
        filter state at click time. At the 500 ms default, a term typed and
        followed by an Export click inside that window is not in the filter model
        yet, so the dialog counts -- and the file carries -- the PRE-search rows.
        The dialog is modal, so the grid finishes debouncing behind it and the two
        visibly disagree. Dealer ID and Status are plain React state and
        recompute synchronously, so this is the only input on which "the file is
        what the grid is showing" could be false, and it fails toward MORE people
        in a mailing tool. The list is client-side and pages at 10; there is no
        perf reason for the delay.
      */}
      <GridToolbarQuickFilter debounceMs={0} />
      <Box sx={{ flexGrow: 1 }} />
      {/* Dealer IDs are numeric, so a typed letter could only ever match nothing. */}
      <TextField
        label="Dealer ID"
        size="small"
        value={dealerIdFilter}
        onChange={(e) =>
          onDealerIdFilterChange(e.target.value.replace(/\D/g, ""))
        }
        sx={{ minWidth: 140 }}
      />
      {/*
        Export status is the DEALER's ServiceTitan export state, so this filter selects whole
        dealers -- every user under one dealer carries the same value. "None" is the dealers
        vwOnboardedDealers carries no ACTIVE row for: never onboarded, or a tenant with no
        tblTransmissionDetails row, plus the admin rows, which belong to no dealer; without the
        option those rows are only reachable through All. An ONBOARDED Workiz or vendor dealer is
        NOT in this bucket even though it has no tenant -- the view emits 'Completed' for it, and
        this screen shows what Dealer Lookup shows. userList.js's EXPORT_STATUS_NONE is the rule.
      */}
      {/*
        "ST Data Pull", not "Export Status": this is invoiceStatus as vwOnboardedDealers reports
        it, which for a ServiceTitan dealer is tblTenant.invoiceStatus -- the field Dealer Lookup
        already owns the edit for and labels that way. One DB field must not carry two
        admin-facing names -- and on THIS screen "Export Status" would sit a couple of hundred
        pixels from the Export button and read as the status of the CSV download. The internal
        exportStatus / EXPORT_STATUS_* identifiers keep their names; this is only about the four
        strings an admin actually reads.
      */}
      <FormControl size="small" sx={{ minWidth: 165 }}>
        <InputLabel id="users-export-status-filter-label">
          ST Data Pull
        </InputLabel>
        <Select
          labelId="users-export-status-filter-label"
          label="ST Data Pull"
          value={exportStatusFilter}
          onChange={(e) => onExportStatusFilterChange(e.target.value)}
        >
          <MenuItem value={EXPORT_STATUS_ALL}>All</MenuItem>
          {EXPORT_STATUS_VALUES.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
          <MenuItem value={EXPORT_STATUS_NONE}>None</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="users-status-filter-label">Status</InputLabel>
        <Select
          labelId="users-status-filter-label"
          label="Status"
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
        >
          <MenuItem value={STATUS_ALL}>All</MenuItem>
          <MenuItem value={STATUS_ACTIVE}>Active</MenuItem>
          <MenuItem value={STATUS_INACTIVE}>InActive</MenuItem>
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<DownloadIcon />}
        onClick={onExportClick}
      >
        Export
      </Button>
    </GridToolbarContainer>
  );
};

const UsersListGrid = ({
  editUser,
  onPasswordChange,
  onViewEmailLog,
  usersList,
}) => {
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [dealerIdFilter, setDealerIdFilter] = useState("");
  const [exportStatusFilter, setExportStatusFilter] = useState(EXPORT_STATUS_ALL);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [includeNotOptedIn, setIncludeNotOptedIn] = useState(false);
  const [matchedRows, setMatchedRows] = useState([]);
  const apiRef = useGridApiRef();

  // The TOOLBAR-filtered set: status + dealer ID + export status, and nothing else. Named apart
  // from the grid-filtered set that getFilteredGridRows(apiRef) returns, which is all of this PLUS
  // the quick filter and is what the export must read. Swapping one for the other looks like a
  // no-op and silently drops the quick filter from the file -- the same failure as the debounce
  // race, through a different door -- so the two must not both be called "the filtered users".
  const toolbarFilteredUsers = useMemo(
    () => filterUsers(usersList, statusFilter, dealerIdFilter, exportStatusFilter),
    [usersList, statusFilter, dealerIdFilter, exportStatusFilter]
  );

  // Narrowing the list while on a later page would otherwise leave the admin
  // staring at an empty grid rather than their results.
  const resetToFirstPage = () =>
    setPaginationModel((model) => ({ ...model, page: 0 }));

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const handleDealerIdFilterChange = (value) => {
    setDealerIdFilter(value);
    resetToFirstPage();
  };

  const handleExportStatusFilterChange = (value) => {
    setExportStatusFilter(value);
    resetToFirstPage();
  };

  // No row selection by design: Export always covers every matching row, so
  // there is no path on which an admin exports one page thinking it was all.
  const handleExportClick = () => {
    setMatchedRows(getFilteredGridRows(apiRef));
    // Widening the file to people who never opted in is a decision taken for ONE announcement,
    // not a preference. Reopening the dialog starts from the opted-in-only default again, so a
    // choice made for a service bulletin cannot ride along unnoticed into the next marketing send.
    setIncludeNotOptedIn(false);
    setExportDialogOpen(true);
  };

  const exportRows = buildExportRows(matchedRows, { includeNotOptedIn });
  // Counted with the predicate the dialog names, not as the difference between
  // the two lengths -- the sentence below asserts a CAUSE, so it has to be
  // counted by that cause rather than by whatever happens to have been dropped.
  const notOptedInCount = matchedRows.filter((row) => !isMailable(row)).length;

  const handleConfirmExport = () => {
    exportFromJSON({
      data: exportRows,
      // Name every choice that shaped the file -- the dealer, the export status, and whether it
      // carries people who never opted in -- so exporting several in a sitting does not leave a
      // folder of files that only differ by timestamp. A token appears only when that choice is
      // not the default, so the ordinary unfiltered export keeps the name it always had.
      fileName: [
        "jb360-users",
        statusFilter.toLowerCase(),
        dealerIdFilter.trim() ? `dealer${dealerIdFilter.trim()}` : null,
        exportStatusFilter !== EXPORT_STATUS_ALL
          ? `exportstatus-${exportStatusFilter.toLowerCase()}`
          : null,
        includeNotOptedIn ? "withnotoptedin" : null,
        Date.now(),
      ]
        .filter(Boolean)
        .join("-"),
      exportType: exportFromJSON.types.csv,
    });
    setExportDialogOpen(false);
  };

  const columns = [
    {
      field: "dealerAvatar",
      headerName: "Dealer Image",
      width: 110,
      renderCell: (param) => {
        return (
          <Avatar
            name={param.value.replace(/[^a-zA-Z ]/g, "")}
            maxInitials={2}
            size="60"
            round={true}
            style={{ marginTop: "5px", marginBottom: "5px" }}
          />
        );
      },
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "dealerName",
      headerName: "Dealer Name",
      width: 180,
      sortable: false,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "dealerId",
      headerName: "Dealer ID",
      width: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "exportStatus",
      headerName: "ST Data Pull",
      // Narrow on purpose. Rows are auto-height, so the one long value -- a dealer sitting at two
      // statuses, "Exported, Posted" -- wraps onto a second line rather than costing every other
      // column 40px of width it does not get back.
      width: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
      // A dealer vwOnboardedDealers carries no ACTIVE row for has no export status at all, and an
      // empty cell there reads as something that failed to load -- say it with a dash instead.
      // Having no tenant is NOT that case: an onboarded Workiz or vendor dealer has none and still
      // shows 'Completed', because the view emits that literal for it. A dealer whose active
      // tenants DISAGREE arrives as "Exported, Posted": both are true of the dealer, so the cell
      // shows both rather than picking one and hiding the other.
      renderCell: (param) => (param.value ? param.value : "\u2014"),
    },
    {
      field: "registeredName",
      headerName: "Registered Name",
      // Narrower than the email beside it: a name fits in this, an address rarely does, so the
      // 20px is worth more there. The pair still costs the row exactly what it did before.
      width: 180,
      sortable: false,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "registeredEmail",
      headerName: "Registered Email",
      width: 200,
      sortable: false,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "userRole",
      headerName: "Role",
      width: 80,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "isPrimary",
      headerName: "Primary",
      width: 80,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => {
        return (
          <>
            {param.value === 1 ?
              <Tooltip title="Primary Contact"><StarsIcon color="secondary" /></Tooltip>
              : ""}
          </>


        );
      },
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 110,
      sortable: false,
      renderCell: (param) => {
        return (
          <JBWDateLabel inputDate={param.value} dateFormat="MMM.DD,YYYY" />
        );
      },
      headerAlign: "center",
      align: "center",
    },
    {
      field: "isActive",
      headerName: "Status",
      // Wide enough for the chip to spell "InActive" -- at 120 the longer of the two words was
      // clipped, which is the one an admin is most often scanning for.
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => {
        const bgColor = param.value === 0 ? "#DEB729" : "#4ECC72";
        //console.log("inside active colums", param);
        return (
          <Chip
            sx={{
              color: "#fff",
              backgroundColor: bgColor,
              mt: 1,
              mb: 1,
              width: "70%",
            }}
            label={param.value === 0 ? "InActive" : "Active"}
          />
        );
      },
      sortable: false,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      cellClassName: "actions",
      width: 130,
      headerAlign: "center",
      align: "center",
      sortable: false,
      getActions: (param) => {
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEdit(param)}
            color="primary"
            title="Edit User"
          />,
          param.row.isActive === 1 ? (
            <GridActionsCellItem
              key="changePassword"
              icon={<PasswordIcon />}
              label="Resend Invite / Reset Password"
              onClick={handleChangePassword(param)}
              color="secondary"
              title="Resend Invite / Reset Password"
            />
          ) : (
            <Tooltip
              key="changePasswordDisabled"
              title="Activate the user before reseting the password."
            >
              <span>
                <GridActionsCellItem
                  icon={<PasswordIcon />}
                  label="Resend Invite / Reset Password"
                  disabled
                  showInMenu={false}
                />
              </span>
            </Tooltip>
          ),
          <GridActionsCellItem
            key="emailLog"
            icon={<EmailIcon />}
            label="Email History"
            onClick={handleViewEmailLog(param)}
            color="primary"
            title="Email History"
          />,
        ];
      },
    },
  ];

  const handleEdit = (param) => () => {
    console.log("Edit clicked ", param.row);
    editUser(param.row);
  };

  const handleChangePassword = (param) => () => {
    console.log("change password clicked ", param.row);
    onPasswordChange(param.row);
  };

  const handleViewEmailLog = (param) => () => {
    onViewEmailLog(param.row);
  };

  return (
    <>
      <DataGrid
        rows={toolbarFilteredUsers}
        apiRef={apiRef}
        getRowId={(row) => row.id}
        columns={columns}
        autoHeight
        getRowHeight={() => "auto"}
        disableColumnFilter={true}
        disableColumnMenu={true}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
          toolbar: QuickSearchToolbar,
        }}
        slotProps={{
          toolbar: {
            statusFilter,
            onStatusFilterChange: handleStatusFilterChange,
            dealerIdFilter,
            onDealerIdFilterChange: handleDealerIdFilterChange,
            exportStatusFilter,
            onExportStatusFilterChange: handleExportStatusFilterChange,
            onExportClick: handleExportClick,
          },
        }}
        pageSizeOptions={[10]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        // Only the quick filter can raise this -- disableColumnFilter is on --
        // and it needs the same page reset as the two controls beside it.
        onFilterModelChange={resetToFirstPage}
        sx={{
          "& .muidatagrid-colcelltitle ": {
            display: "block",
            textAlign: "center",
            width: "100%",
          },
        }}
      />
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Export user list</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            <Box>
              {exportRows.length} user{exportRows.length === 1 ? "" : "s"} will be
              exported.
            </Box>
            {notOptedInCount > 0 && (
              <Box sx={{ mt: 1 }}>
                {notOptedInCount} user{notOptedInCount === 1 ? "" : "s"} matching
                the current filters {notOptedInCount === 1 ? "is" : "are"} not
                opted in to email notifications and will
                {includeNotOptedIn ? " be included." : " not be included."}
              </Box>
            )}
          </DialogContentText>
          {/*
            Unchecked every time the dialog opens. The people it adds never agreed to be mailed,
            so including them has to be an act rather than a leftover setting -- and the count
            above restates what the box currently means before anything is written.
          */}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={includeNotOptedIn}
                onChange={(e) => setIncludeNotOptedIn(e.target.checked)}
              />
            }
            label="Include users not opted in to email notifications"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmExport}
            disabled={exportRows.length === 0}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UsersListGrid;
